/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Settings as SettingsIcon, Building2, User, FileText, ScrollText } from 'lucide-react';
import {
  OrganizationSettings,
  DSASettings,
  DashboardSettings,
  ChangesSettings,
  CodingAgentSettings,
  SourceControlSettings,
  GitHubSettings,
  GitLabSettings,
  LinearSettings,
  JiraSettings,
  ChangelogSettings,
  LogsSettings,
  VelocityMcpInstallSettings,
} from '../components/settings';

type SettingsTab = 'user' | 'organization' | 'changelog' | 'logs';

export function Settings() {
  const { sourceControlProvider } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Active tab state - initialize from URL param or default to 'user'
  const tabParam = searchParams.get('tab') as SettingsTab | null;
  const [activeTab, setActiveTab] = useState<SettingsTab>(
    tabParam && ['user', 'organization', 'changelog', 'logs'].includes(tabParam) ? tabParam : 'user'
  );

  // Update URL when tab changes
  const handleTabChange = (tab: SettingsTab) => {
    setActiveTab(tab);
    if (tab === 'user') {
      // Remove tab param for default user tab to keep URL clean
      searchParams.delete('tab');
    } else {
      searchParams.set('tab', tab);
    }
    setSearchParams(searchParams);
  };

  // Sync tab from URL when navigating directly with ?tab= param
  useEffect(() => {
    if (tabParam && ['user', 'organization', 'changelog', 'logs'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px' }}>
          <SettingsIcon style={{ marginRight: '12px', color: '#0066cc' }} />
          <h2 style={{ margin: 0 }}>Settings</h2>
        </div>

        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          borderBottom: '2px solid #e5e7eb',
          marginBottom: '24px',
          gap: '4px'
        }}>
          <button
            onClick={() => handleTabChange('user')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === 'user' ? '2px solid #0066cc' : '2px solid transparent',
              marginBottom: '-2px',
              color: activeTab === 'user' ? '#0066cc' : '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            <User size={16} />
            User Settings
          </button>
          <button
            onClick={() => handleTabChange('organization')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === 'organization' ? '2px solid #0066cc' : '2px solid transparent',
              marginBottom: '-2px',
              color: activeTab === 'organization' ? '#0066cc' : '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            <Building2 size={16} />
            Organization
          </button>
          <button
            onClick={() => handleTabChange('changelog')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === 'changelog' ? '2px solid #0066cc' : '2px solid transparent',
              marginBottom: '-2px',
              color: activeTab === 'changelog' ? '#0066cc' : '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            <FileText size={16} />
            Changelog
          </button>
          <button
            onClick={() => handleTabChange('logs')}
            style={{
              padding: '12px 24px',
              fontSize: '14px',
              fontWeight: 500,
              border: 'none',
              background: 'transparent',
              borderBottom: activeTab === 'logs' ? '2px solid #0066cc' : '2px solid transparent',
              marginBottom: '-2px',
              color: activeTab === 'logs' ? '#0066cc' : '#6b7280',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s ease'
            }}
          >
            <ScrollText size={16} />
            Logs
          </button>
        </div>

        {/* Organization Settings Tab */}
        {activeTab === 'organization' && (
          <OrganizationSettings />
        )}

        {/* Changelog Tab */}
        {activeTab === 'changelog' && (
          <ChangelogSettings />
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <LogsSettings />
        )}

        {/* User Settings Tab */}
        {activeTab === 'user' && (
          <>
            {/* Velocity MCP Installation Panel */}
            <VelocityMcpInstallSettings />

            {/* DSA Settings Section */}
            <DSASettings />

            {/* Dashboard Settings Section */}
            <DashboardSettings />

            {/* Changes Settings Section */}
            <ChangesSettings />

            {/* Coding Agent Configuration Section */}
            <CodingAgentSettings />

            {/* Source Control Provider Selection */}
            <SourceControlSettings />

            {/* GitHub Integration Section */}
            {sourceControlProvider === 'github' && (
              <GitHubSettings />
            )}

            {/* GitLab Integration Section */}
            {sourceControlProvider === 'gitlab' && (
              <GitLabSettings />
            )}

            {/* Linear Integration Section */}
            <LinearSettings />

            {/* JIRA Integration Section */}
            <JiraSettings />
          </>
        )}
      </div>
    </div>
  );
}
